/* ============================================================
   AURORA HOMEPAGE HERO CAROUSEL — controller
   Engine: the theme's existing Swiper build (assets/swiper.js).
   Slide 2 "Nine Living Reflections": nine sequential studio-light
   reflection passes built as an inline SVG from the coordinate
   configuration below, replayed each time the slide becomes active.
   No permanent requestAnimationFrame loop — CSS animations only.
   ============================================================ */
(function () {
  'use strict';

  /* ================================================================
     NINE LIVING REFLECTIONS — COORDINATE CONFIGURATION (spec 5.3/12)
     One documented object; the single source of truth for both maps.

     viewBox: desktop 0 0 1536 864 (matches the approved desktop JPG),
              mobile  0 0 864 1536 (matches the approved mobile JPG).
     Order:   pass 1 → 9 runs sequentially through the composition and
              MUST finish on the dominant foreground model (entry 9).
     Fields per pass:
       shapes  2–4 soft ellipses per model (face / shoulder zones):
               cx, cy, rx, ry, rot (degrees) in viewBox units.
       peak    max group opacity — calibrated per complexion so darker
               skin is never greyed and lighter skin never clips.

     ⚠ CALIBRATION NOTE (2026-07-19): the CDN is unreachable from this
     build environment, so these default positions follow a classic
     nine-portrait hero arrangement (staggered back row → mid row →
     dominant foreground centre-right) and MUST be visually calibrated
     against the real photograph in one review pass. Edit ONLY this
     object — nothing else in the animation needs to change.
     ================================================================ */
  var AURORA_HERO_REFLECTIONS = {
    stagger: 480,     /* ms between passes — owner 2026-07-19 (final): the
                         full nine-light sequence lasts ~5 seconds and the
                         slide holds until it completes */
    duration: 900,    /* ms per pass (overlapping) */
    firstDelay: 300,  /* ms — first reflection begins */
    desktop: [
      /* CALIBRATED 2026-07-19 against the real photograph (owner desktop
         screenshot). Order runs through the composition and finishes on
         the dominant right-side model. Peaks tuned per complexion for
         the screen blend: deeper skin lower peak, never greyed. */
      { peak: 0.36, shapes: [{ cx: 638, cy: 455, rx: 46, ry: 62, rot: -10 }, { cx: 600, cy: 562, rx: 58, ry: 30, rot: -6 }] },   /* 1 left, dark hair */
      { peak: 0.38, shapes: [{ cx: 535, cy: 700, rx: 46, ry: 60, rot: 8 }, { cx: 478, cy: 792, rx: 60, ry: 32, rot: 5 }] },      /* 2 lower left, blonde */
      { peak: 0.30, shapes: [{ cx: 850, cy: 175, rx: 46, ry: 64, rot: -8 }, { cx: 862, cy: 292, rx: 54, ry: 30, rot: -4 }] },    /* 3 top centre, deep skin */
      { peak: 0.34, shapes: [{ cx: 890, cy: 410, rx: 46, ry: 60, rot: 10 }, { cx: 938, cy: 520, rx: 58, ry: 30, rot: 6 }] },     /* 4 centre, tan */
      { peak: 0.36, shapes: [{ cx: 795, cy: 645, rx: 46, ry: 60, rot: -9 }, { cx: 740, cy: 758, rx: 58, ry: 30, rot: -5 }] },    /* 5 lower centre, brunette */
      { peak: 0.38, shapes: [{ cx: 1050, cy: 120, rx: 44, ry: 58, rot: 9 }, { cx: 1078, cy: 228, rx: 54, ry: 28, rot: 5 }] },    /* 6 top right, pale */
      { peak: 0.36, shapes: [{ cx: 1050, cy: 480, rx: 46, ry: 60, rot: -8 }, { cx: 1010, cy: 590, rx: 58, ry: 30, rot: -4 }] },  /* 7 centre right, freckles */
      { peak: 0.30, shapes: [{ cx: 1005, cy: 745, rx: 46, ry: 60, rot: 11 }, { cx: 1078, cy: 822, rx: 58, ry: 30, rot: 6 }] },   /* 8 lower right, deep skin */
      /* 9 — dominant foreground model, right side, larger zones */
      { peak: 0.45, shapes: [{ cx: 1310, cy: 370, rx: 66, ry: 92, rot: -8 }, { cx: 1382, cy: 560, rx: 92, ry: 46, rot: -5 }, { cx: 1252, cy: 520, rx: 54, ry: 30, rot: 10 }] }
    ],
    mobile: [
      { peak: 0.42, shapes: [{ cx: 165, cy: 330, rx: 42, ry: 56, rot: -12 }, { cx: 190, cy: 440, rx: 56, ry: 30, rot: -6 }] },
      { peak: 0.38, shapes: [{ cx: 430, cy: 290, rx: 42, ry: 56, rot: 8 }, { cx: 410, cy: 400, rx: 54, ry: 28, rot: 4 }] },
      { peak: 0.45, shapes: [{ cx: 695, cy: 335, rx: 42, ry: 56, rot: -8 }, { cx: 715, cy: 445, rx: 54, ry: 28, rot: -4 }] },
      { peak: 0.40, shapes: [{ cx: 235, cy: 620, rx: 44, ry: 58, rot: 10 }, { cx: 215, cy: 735, rx: 56, ry: 30, rot: 6 }] },
      { peak: 0.44, shapes: [{ cx: 620, cy: 640, rx: 44, ry: 58, rot: -10 }, { cx: 645, cy: 755, rx: 56, ry: 30, rot: -5 }] },
      { peak: 0.38, shapes: [{ cx: 165, cy: 935, rx: 44, ry: 58, rot: 9 }, { cx: 190, cy: 1050, rx: 56, ry: 30, rot: 5 }] },
      { peak: 0.42, shapes: [{ cx: 690, cy: 955, rx: 44, ry: 58, rot: -9 }, { cx: 665, cy: 1070, rx: 56, ry: 30, rot: -4 }] },
      { peak: 0.40, shapes: [{ cx: 425, cy: 860, rx: 44, ry: 58, rot: 11 }, { cx: 445, cy: 975, rx: 56, ry: 30, rot: 6 }] },
      /* 9 — dominant foreground model, lower centre, larger zones */
      { peak: 0.48, shapes: [{ cx: 432, cy: 1160, rx: 58, ry: 78, rot: -8 }, { cx: 470, cy: 1305, rx: 84, ry: 42, rot: -5 }, { cx: 375, cy: 1280, rx: 50, ry: 28, rot: 10 }] }
    ]
  };

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function buildReflectionSvg(map, viewBox, extraClass) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice'); /* crops exactly like object-fit: cover */
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'yhc-reflections ' + extraClass);

    var defs = document.createElementNS(SVG_NS, 'defs');
    var filter = document.createElementNS(SVG_NS, 'filter');
    var fid = 'yhcBlur-' + extraClass.replace(/[^a-z-]/gi, '');
    filter.setAttribute('id', fid);
    filter.setAttribute('x', '-60%'); filter.setAttribute('y', '-60%');
    filter.setAttribute('width', '220%'); filter.setAttribute('height', '220%');
    var blur = document.createElementNS(SVG_NS, 'feGaussianBlur');
    blur.setAttribute('stdDeviation', '22'); /* fixed — blur values never animate (spec 5.3) */
    filter.appendChild(blur);
    defs.appendChild(filter);
    svg.appendChild(defs);

    for (var i = 0; i < map.length; i++) {
      var pass = map[i];
      var g = document.createElementNS(SVG_NS, 'g');
      g.style.setProperty('--yhc-refl-peak', String(pass.peak));
      g.style.setProperty('--yhc-refl-dur', AURORA_HERO_REFLECTIONS.duration + 'ms');
      g.style.animationDelay = (AURORA_HERO_REFLECTIONS.firstDelay + i * AURORA_HERO_REFLECTIONS.stagger) + 'ms';
      for (var s = 0; s < pass.shapes.length; s++) {
        var sh = pass.shapes[s];
        var el = document.createElementNS(SVG_NS, 'ellipse');
        el.setAttribute('cx', sh.cx); el.setAttribute('cy', sh.cy);
        el.setAttribute('rx', sh.rx); el.setAttribute('ry', sh.ry);
        el.setAttribute('transform', 'rotate(' + (sh.rot || 0) + ' ' + sh.cx + ' ' + sh.cy + ')');
        el.setAttribute('fill', '#ffffff');
        el.setAttribute('filter', 'url(#' + fid + ')');
        g.appendChild(el);
      }
      svg.appendChild(g);
    }
    return svg;
  }

  function init() {
    var root = document.querySelector('[data-yhc]');
    if (!root || typeof window.Swiper !== 'function') return;
    var swiperEl = root.querySelector('.swiper');
    if (!swiperEl) return;

    /* Build the SVG reflection overlays from the configuration ONLY when
       the block's animation style is "reflections" (skipped entirely under
       reduced motion — spec 11). The default "dawn" sweep is pure CSS
       (.yhc-dawn, rendered by the section) and needs no build step. */
    var zonesSettle = root.querySelector('[data-yhc-zones-settle]');
    if (zonesSettle && !reducedMotion.matches && zonesSettle.getAttribute('data-yhc-animate') === 'true' &&
        zonesSettle.getAttribute('data-yhc-style') === 'reflections') {
      zonesSettle.appendChild(buildReflectionSvg(AURORA_HERO_REFLECTIONS.desktop, '0 0 1536 864', 'yhc-reflections--desktop'));
      zonesSettle.appendChild(buildReflectionSvg(AURORA_HERO_REFLECTIONS.mobile, '0 0 864 1536', 'yhc-reflections--mobile'));
    }

    /* ---- SLIDE 2 PLAYBACK CORE (clean rebuild, owner 2026-07-20:
       "play immediately and run till the end") ----
       One hardened routine every code path funnels through. Each step
       is isolated so no failed step can silently cancel the next:
       the old code rewound and played inside ONE try block — when
       currentTime=0 threw before metadata arrived, play() was skipped
       and the film sat frozen on its grey opening frame. */
    function ensurePlays(vid, rewind) {
      if (!vid) return;
      try { vid.muted = true; } catch (e0) { /* noop */ }
      /* rewind only when the pipeline can seek; a fresh element is at 0 anyway */
      if (rewind) {
        try { if (vid.readyState >= 1 && vid.currentTime > 0.05) vid.currentTime = 0; } catch (e1) { /* noop */ }
      }
      /* dead pipeline (no data, nothing loading) -> restart the fetch */
      try { if (vid.readyState === 0 && vid.networkState !== 2) vid.load(); } catch (e2) { /* noop */ }
      try {
        var p = vid.play();
        if (p && p.catch) p.catch(function () {
          /* autoplay refused before data was ready: retry the moment a
             frame is decodable */
          vid.addEventListener('canplay', function once() {
            vid.removeEventListener('canplay', once);
            try { vid.play().catch(function () {}); } catch (e3) { /* noop */ }
          });
        });
      } catch (e4) { /* noop */ }
    }

    function activate(slide) {
      /* Restart the slide's entrance (camera settle, nine reflections,
         text) each time it becomes the active slide — once per
         activation (spec 5.4 "replay once only when it becomes active
         again"). */
      var slides = root.querySelectorAll('.yhc-slide');
      for (var i = 0; i < slides.length; i++) slides[i].classList.remove('is-live');
      if (!slide) return;
      void slide.offsetWidth; /* one reflow so re-adding the class restarts CSS animations */
      slide.classList.add('is-live');
      /* Pause / play slide videos so hidden slides do no work (spec 11).
         A video slide HOLDS until its video ends (owner 2026-07-19):
         autoplay pauses while it is active and the 'ended' listener
         advances the carousel. */
      var hasVideo = false;
      var vids = root.querySelectorAll('.yhc-slide video');
      for (var v = 0; v < vids.length; v++) {
        var owned = slide.contains(vids[v]);
        if (owned) { hasVideo = true; ensurePlays(vids[v], true); }
        else { try { vids[v].pause(); } catch (err) { /* noop */ } }
      }
      if (swiperRef && swiperRef.autoplay) {
        if (hasVideo) { swiperRef.autoplay.stop(); }
        else if (!reducedMotion.matches) { try { swiperRef.autoplay.start(); } catch (err2) { /* noop */ } }
      }
      /* Watchdog (owner 2026-07-20: slide 2 froze on some machines —
         browser battery/energy savers can refuse video autoplay). If the
         film has not actually started ~2.5s after activation, retry it
         once and RESTORE the timed rotation so the carousel never jams
         on a slide whose video cannot play. */
      if (hasVideo) {
        if (videoWatchdog) clearTimeout(videoWatchdog);
        videoWatchdog = setTimeout(function () {
          var vid = slide.querySelector('video');
          if (!vid || vid.currentTime > 0.2) return;
          try { vid.load(); } catch (eW) { /* noop */ }
          ensurePlays(vid, false);
          if (swiperRef && swiperRef.autoplay && !reducedMotion.matches) {
            try { swiperRef.autoplay.start(); } catch (eW2) { /* noop */ }
          }
        }, 2500);
      }
    }
    var videoWatchdog = null;

    /* Visit rotation removed (owner 2026-07-25): starting on a different
       slide each visit made the hero look like it jumped on load. The
       carousel now always opens on the first slide in the editor order. */
    var startIndex = 0;

    var swiperRef = null;
    var swiperParams = {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: 850,
      rewind: true,
      initialSlide: startIndex,
      autoplay: reducedMotion.matches ? false : {
        /* Holds each slide past the ~5.0s Nine Living Reflections
           sequence — the lights always complete before advancing */
        delay: 7500,
        disableOnInteraction: true,
        pauseOnMouseEnter: true
      },
      keyboard: { enabled: true, onlyInViewport: true },
      pagination: {
        el: root.querySelector('.swiper-pagination'),
        clickable: true,
        bulletElement: 'button'
      },
      a11y: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        paginationBulletMessage: 'Go to slide {{index}}'
      },
      on: {
        afterInit: function (sw) {
          swiperRef = sw;
          activate(sw.slides[sw.activeIndex]);
        },
        slideChange: function (sw) {
          activate(sw.slides[sw.activeIndex]);
        }
      }
    };
    /* Arrow controls are optional (owner 2026-07-19: dots only) — the
       navigation module is configured only when the elements exist. */
    var prevEl = root.querySelector('[data-yhc-prev]');
    var nextEl = root.querySelector('[data-yhc-next]');
    if (prevEl && nextEl) swiperParams.navigation = { prevEl: prevEl, nextEl: nextEl };
    var swiper = new window.Swiper(swiperEl, swiperParams);

    swiperRef = swiper;
    /* Video slides advance when their video finishes */
    var allVids = root.querySelectorAll('.yhc-slide video');
    for (var av = 0; av < allVids.length; av++) {
      (function (vid) {
        vid.addEventListener('ended', function () {
          var slide = vid.closest('.swiper-slide');
          if (slide && slide.classList.contains('swiper-slide-active')) {
            swiper.slideNext();
          }
        });
        /* once the film REALLY runs, reclaim the hold-until-ended rule
           (the watchdog may have restored timed rotation as a failsafe) */
        vid.addEventListener('playing', function () {
          var slide = vid.closest('.swiper-slide');
          if (slide && slide.classList.contains('swiper-slide-active') &&
              swiper.autoplay && vid.currentTime > 0.05) {
            swiper.autoplay.stop();
          }
        });
      })(allVids[av]);
    }
    /* tab returns: re-kick the active slide's film if it was suspended */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) return;
      var act = root.querySelector('.swiper-slide-active video');
      if (act && act.paused && !act.ended) ensurePlays(act, false);
    });
    /* a mid-stream network stall also gets an immediate kick */
    for (var sv = 0; sv < allVids.length; sv++) {
      (function (vid) {
        vid.addEventListener('stalled', function () {
          var slide = vid.closest('.swiper-slide');
          if (slide && slide.classList.contains('swiper-slide-active')) ensurePlays(vid, false);
        });
      })(allVids[sv]);
    }
    /* persistent keeper (owner 2026-07-20: the zones film froze on its
       grey opening frame on some machines): as long as the active slide
       carries a film that is paused or has never produced a frame, keep
       nudging it through the same hardened routine. */
    setInterval(function () {
      if (document.hidden) return;
      var act = root.querySelector('.swiper-slide-active video');
      if (!act || act.ended) return;
      var neverStarted = act.currentTime < 0.1 && act.readyState < 2;
      if (!act.paused && !neverStarted) return;
      ensurePlays(act, false);
    }, 1200);

    /* GESTURE UNLOCKER (owner 2026-07-20: films frozen on devices whose
       browser refuses ALL programmatic autoplay — iOS Low Power Mode,
       Chrome energy/data saver). A play() issued during a real user
       gesture is always allowed, so the first tap / touch / key press
       anywhere revives the active slide's film. Listeners detach after
       the first successful start. */
    (function () {
      var unlocked = false;
      function unlock() {
        if (unlocked) return;
        var act = root.querySelector('.swiper-slide-active video');
        if (act && act.paused && !act.ended) {
          try { act.muted = true; act.play().then(function () { unlocked = true; detach(); }).catch(function () {}); }
          catch (eU) { /* noop */ }
        }
      }
      function detach() {
        document.removeEventListener('touchstart', unlock, true);
        document.removeEventListener('pointerdown', unlock, true);
        document.removeEventListener('keydown', unlock, true);
      }
      document.addEventListener('touchstart', unlock, true);
      document.addEventListener('pointerdown', unlock, true);
      document.addEventListener('keydown', unlock, true);
    })();

    /* Reduced-motion flips at runtime: stop autoplay immediately */
    var onRmChange = function () {
      if (reducedMotion.matches && swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();
    };
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onRmChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
